/**
 * useAdminActivity Hook
 *
 * Tracks user activity and manages session inactivity timeout.
 * Sessions expire after 20 minutes of inactivity.
 *
 * Features:
 * - Tracks mousemove, keypress, click, scroll events
 * - Debounces activity pings (max once per minute)
 * - Shows warning modal 2 minutes before expiration
 * - Auto-logout when session expires
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import adminApi from '../services/adminApi';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
const PING_DEBOUNCE_MS = 60000; // Ping server max once per minute
const WARNING_THRESHOLD_SECONDS = 120; // Show warning when 2 minutes remaining

// Candidate evidence is intentionally read-only. Its proxy rejects every write
// except password authentication, so do not generate the normal session-activity
// mutation in an evidence build. Production builds do not set this flag.
export const ADMIN_ACTIVITY_PING_ENABLED =
  process.env.REACT_APP_ADMIN_EVIDENCE_READ_ONLY !== '1';

/**
 * Hook for tracking admin activity and session timeout
 *
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {Function} onLogout - Function to call when session expires
 * @returns {Object} { showWarning, dismissWarning, remainingSeconds, refreshSession }
 */
export function useAdminActivity(isAuthenticated, onLogout) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(1200); // 20 minutes

  const lastPingRef = useRef(0);
  const countdownIntervalRef = useRef(null);
  const warningDismissedRef = useRef(false);

  // Ping server to update session activity
  const pingServer = useCallback(async () => {
    if (!isAuthenticated || !ADMIN_ACTIVITY_PING_ENABLED) return;

    const now = Date.now();
    if (now - lastPingRef.current < PING_DEBOUNCE_MS) {
      return; // Debounce - don't ping too frequently
    }

    lastPingRef.current = now;

    try {
      // Use adminApi which has login grace period protection for 401 handling
      const response = await adminApi.post('/api/admin/ping', {});
      const data = await adminApi.getJson(response);
      if (data.session_remaining_seconds !== undefined) {
        setRemainingSeconds(data.session_remaining_seconds);
        warningDismissedRef.current = false; // Reset warning dismissed state on activity
      }
    } catch (err) {
      // adminApi handles 401 errors with grace period protection.
      // If we still get an error here (after grace period), it's a real session expiration.
      // However, don't immediately log out - let the countdown timer handle it naturally.
      // This prevents race conditions during login.
      console.warn('Session ping failed:', err.message);
    }
  }, [isAuthenticated]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;
    pingServer();
  }, [isAuthenticated, pingServer]);

  // Refresh session manually (e.g., when user clicks "Stay logged in")
  const refreshSession = useCallback(async () => {
    lastPingRef.current = 0; // Reset debounce to allow immediate ping
    await pingServer();
    setShowWarning(false);
    warningDismissedRef.current = false;
  }, [pingServer]);

  // Dismiss warning temporarily
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    warningDismissedRef.current = true;
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Add event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial ping to get session status
    pingServer();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, handleActivity, pingServer]);

  // Countdown timer
  useEffect(() => {
    if (!isAuthenticated) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        const newValue = Math.max(0, prev - 1);

        // Check if we should show warning
        if (newValue <= WARNING_THRESHOLD_SECONDS && newValue > 0 && !warningDismissedRef.current) {
          setShowWarning(true);
        }

        // Check if session has expired
        if (newValue === 0) {
          setShowWarning(false);
          onLogout?.();
        }

        return newValue;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, onLogout]);

  return {
    showWarning,
    dismissWarning,
    remainingSeconds,
    refreshSession,
  };
}

export default useAdminActivity;
